import React, { useEffect, useMemo, useRef, useState } from 'react';
import DraggableContainer from './draggablecontainer';
import TypedText from './typedtext';
import { FaUserEdit } from 'react-icons/fa';
import styled from 'styled-components';
import { updateBusinessProfile } from '../../../utils/updateBusinessProfile';
import BusinessDescriptionModal from '../../business/BusinessDescriptionModal';
import UserProfileUpload from '../../user/UserProfileUpload';
import { useProfileStore } from '../../../state/profileStore';
import FloatingToast from '../../common/FloatingToast';
import { getStrings } from '../../../design/designCenterLocale';
import BusinessAddressFields from './BusinessAddressFields';

// Detects whether a stored b_website is a platform-hosted profile URL
// (Google Business Profile, Facebook page, IG, TikTok, etc.) rather than
// the user's actual website domain. Used to surface a hint nudging GBP/
// social-signup users to enter their real website if/when they have one —
// which unlocks the website-domain-match path that the Google Review
// Importer requires. Returns the friendly label of the matched platform,
// or null if it looks like a real domain (or is empty).
function detectPlatformHostedWebsite(url) {
  if (!url || typeof url !== 'string') return null;
  const lower = url.toLowerCase();
  if (/(^https?:\/\/)?(www\.)?google\.com\/maps\//.test(lower)) return 'Google Business Profile';
  if (/(^https?:\/\/)?(g\.page|business\.google\.com)\//.test(lower)) return 'Google Business Profile';
  if (/(^https?:\/\/)?(www\.)?facebook\.com\//.test(lower)) return 'Facebook page';
  if (/(^https?:\/\/)?(www\.)?instagram\.com\//.test(lower)) return 'Instagram profile';
  if (/(^https?:\/\/)?(www\.)?tiktok\.com\//.test(lower)) return 'TikTok profile';
  if (/(^https?:\/\/)?(www\.)?linkedin\.com\//.test(lower)) return 'LinkedIn page';
  return null;
}





const Content = styled.div`
  padding: 12px 24px 12px 24px;
  width: 403px;   /* +15% (was 350) — matches the widened DraggableContainer */
  height: auto;
  background: #ffffff;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  /* NB: scroll lives on the parent ContentArea (in draggablecontainer.jsx),
     not here. The DraggableContainer's Container is fixed at 420px with
     overflow: hidden, so ContentArea is the actual constrained box — that's
     where overflow-y: auto and the scrollbar styling go. Adding them here
     was a no-op because Content's max-height never gets stressed. */
`;


function formatPhoneNumber(input) {
  console.log('📞 Raw input:', input);
  const cleaned = ('' + input).replace(/\D/g, '');
  console.log('✅ Digits only:', cleaned);

  if (cleaned.length === 10) {
    const formatted = `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    console.log('📐 Formatted:', formatted);
    return formatted;
  }

  console.warn('❌ Invalid length, skipping format. Returning original.');
  return input;
}
const OngoingProfileAssistant = ({ onClose, isOwner: isOwnerProp }) => {
  // Individual selectors — see userprofile.jsx for context. This component
  // is mounted whenever the docking station's profile-editor icon is
  // active, so it sits in the render tree all session long. Whole-store
  // destructure re-renders it on every unrelated store change, which
  // (combined with its lazy-hydration useEffect that calls /api/profile/<id>)
  // contributed to the 429 cascade Brett caught.
  const profile = useProfileStore((s) => s.profile);
  const setProfile = useProfileStore((s) => s.setProfile);
  const updateProfile = useProfileStore((s) => s.updateProfile);
  // decide role: explicit prop → store.role
  const effectiveRole = isOwnerProp ? 'owner' : profile?.u_role;
  const isEmployee = effectiveRole !== 'owner';

  const [businessOwnerName, setBusinessOwnerName] = useState('');  
  const tooltipMessage = isEmployee
    ? `${businessOwnerName || 'The business owner'} is the owner of the business information`
    : '';
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [readyToShow, setReadyToShow] = useState(false);
useEffect(() => {
  const frame = requestAnimationFrame(() => setReadyToShow(true));
  return () => cancelAnimationFrame(frame);
}, []);
    const reloadUserFromServer = async () => {
      try {
        const res = await fetch(`/api/profile/${profile.u_id}`, { credentials: 'include' });
        const fresh = await res.json();
        console.log('🌐 Full fresh user:', fresh);
        if (fresh) {
          console.log('🚀 Fresh user before setProfile:', fresh);
          console.log('📦 Current profile before merge:', profile);
          setProfile(fresh); // keep Zustand as the single, complete shape
        }
      } catch (err) {
        console.error('❌ Failed to reload user from server:', err);
      }
    };
  const [descriptionModalOpen, setDescriptionModalOpen] = useState(false);
  console.log('🧠 [Parent] Full user object at modal trigger:', profile);
  const [aboutUsContent, setAboutUsContent] = useState('');

  // ── Enrichment-in-progress banner (polling) ─────────────────────────────
  // Fires on mount if user is a fresh signup whose background enrichment
  // (runPostSignupAnalysis in signup.js) hasn't finished populating
  // b_about_us yet. Polls /api/profile/business/:userId every 5s until:
  //   - b_about_us populates (>= 50 chars) → update profileStore, hide banner
  //   - 90s elapses (18 polls) → hide banner regardless (silent timeout;
  //     user just doesn't see auto-fill — no error, they can fill manually)
  // The setEnrichmentVisible(true) initial check uses both u_is_new_user
  // (existing flag set by signup) AND empty b_about_us so we don't pester
  // returning users who simply haven't filled their about.
  const enrichmentPollRef = useRef(null);
  useEffect(() => {
    if (!profile?.u_id) return;
    const aboutLen = (profile?.b_about_us || '').length;
    const looksLikeFreshSignup = profile?.u_is_new_user === true && aboutLen < 50;
    if (!looksLikeFreshSignup) {
      setEnrichmentVisible(false);
      return;
    }
    setEnrichmentVisible(true);

    let cancelled = false;
    let pollCount = 0;
    const MAX_POLLS = 18; // 18 × 5s = 90s

    const poll = async () => {
      if (cancelled) return;
      pollCount++;
      try {
        const res = await fetch(`/api/profile/business/${profile.u_id}`, { credentials: 'include' });
        if (!res.ok) {
          if (pollCount >= MAX_POLLS) setEnrichmentVisible(false);
          return;
        }
        const fresh = await res.json();
        if (cancelled) return;
        const newAboutLen = (fresh?.b_about_us || '').length;
        if (newAboutLen >= 50) {
          // Enrichment landed. Mirror everything into profileStore so the
          // section editors pick up the fresh values without a page reload.
          updateProfile({
            b_about_us:           fresh.b_about_us,
            b_business_industry:  fresh.b_business_industry,
            b_lat:                fresh.b_lat,
            b_lng:                fresh.b_lng,
            b_address:            fresh.b_address,
            b_city:               fresh.b_city,
            b_state:              fresh.b_state,
            b_zip_code:           fresh.b_zip_code,
            b_phone:              fresh.b_phone,
            b_logo:               fresh.b_logo,
            business: {
              b_about_us:          fresh.b_about_us,
              b_business_industry: fresh.b_business_industry,
              b_lat:               fresh.b_lat,
              b_lng:               fresh.b_lng,
              b_address:           fresh.b_address,
              b_city:              fresh.b_city,
              b_state:             fresh.b_state,
              b_zip_code:          fresh.b_zip_code,
              b_phone:             fresh.b_phone,
              b_logo:              fresh.b_logo,
            },
          });
          setEnrichmentVisible(false);
          if (enrichmentPollRef.current) clearInterval(enrichmentPollRef.current);
          return;
        }
        if (pollCount >= MAX_POLLS) {
          setEnrichmentVisible(false);
          if (enrichmentPollRef.current) clearInterval(enrichmentPollRef.current);
        }
      } catch {
        // Silent — next interval will retry
        if (pollCount >= MAX_POLLS) setEnrichmentVisible(false);
      }
    };

    poll(); // immediate first check
    enrichmentPollRef.current = setInterval(poll, 5000);
    return () => {
      cancelled = true;
      if (enrichmentPollRef.current) clearInterval(enrichmentPollRef.current);
    };
  }, [profile?.u_id, profile?.u_is_new_user, updateProfile]);

  useEffect(() => {
  const fetchBusinessProfile = async () => {
    if (!profile?.u_id) return;

    try {
      const res = await fetch(`/api/profile/business/${profile.u_id}`, { credentials: 'include' });
      const business = await res.json();

      if (res.ok && business) {
        setFormDataBusiness({
          companyName: business.b_name || '',
          address: business.b_address || '',
          city: business.b_city || '',
          state: business.b_state || '',
          subdistrict: business.b_subdistrict || '',
          zip: business.b_zip_code || '',
          country: business.b_country || '',
          addressComponents: business.b_address_components || null,
          phone: business.b_phone || '',
          industry: business.b_business_industry || '',
          website: business.b_website || '',
          showAddress: business.b_show_address ?? true
        });

      }
    } catch (err) {
      console.error('❌ Failed to fetch business profile:', err);
    }
  };

  fetchBusinessProfile();
    // Fetch business owner name
  const fetchOwnerName = async () => {
    if (!profile?.b_id) return; // guard: don't fetch with undefined b_id
    try {
      const res = await fetch(`/api/profile/business/owner/${profile.b_id}`, { credentials: 'include' });
      const result = await res.json();
      if (res.ok && result?.ownerName) {
        setBusinessOwnerName(result.ownerName);
      }
    } catch (err) {
      console.error('❌ Failed to fetch business owner name:', err);
    }
  };

  fetchOwnerName();

}, [profile]);

const [formDataBusiness, setFormDataBusiness] = useState({
  companyName: '',
  address: '',
  city: '',
  state: '',
  subdistrict: '',
  zip: '',
  country: '',
  addressComponents: null,
  phone: '',
  industry: '',
  website: '',
  showAddress: true
});


  const [showMenu, setShowMenu] = useState(false);

  // (Address parsing + the country/field UI now live in BusinessAddressFields.)
  const [activeSection, setActiveSection] = useState(null);
  const [formData, setFormData] = useState({
    firstName: profile?.u_first_name || '',
    lastName: profile?.u_last_name || '',
    jobTitle: profile?.u_job_title || '',
    phoneNumber: profile?.u_phone_number || '',
    // preferredLanguage: '' (empty string) = "Auto-detect" sentinel —
    // sent to backend as null, lets auto-learning fire from explicit
    // brief.tone usage. Otherwise: 'en'|'es'|'ja'|'th'.
    preferredLanguage: profile?.u_preferred_language || '',
  });
  // Replaced the old inline-banner saveMessage (which lived in document flow
  // and shifted every section below it downward on each save) with a typed
  // floating-toast pattern. saveToast: null | { text, type }
  // type ∈ 'success' | 'error' | 'info' — drives the FloatingToast palette.
  const [saveToast, setSaveToast] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Enrichment-in-progress banner state. runPostSignupAnalysis (signup.js
  // background job) takes ~30-60s to populate b_about_us/b_business_industry/
  // b_lat/etc after signup. Without this banner, new users see an empty
  // profile and assume nothing happened. We poll every 5s for up to 90s and
  // hide the banner once b_about_us populates (or timeout). Polling lives
  // here in a separate useEffect below.
  const [enrichmentVisible, setEnrichmentVisible] = useState(false);
  // Derived strings — same locale module as Design Center, picks the user's
  // preferred language from the already-hydrated profileStore.
  const enrichmentMsg = useMemo(
    () => getStrings(profile?.u_preferred_language || 'en').enrichment_banner.message,
    [profile?.u_preferred_language]
  );
  // Sync the language dropdown lazily — useState initializer only runs once
  // on mount, so if the profile finished hydrating AFTER mount (or the signup
  // background job set u_preferred_language=th later), the dropdown stays
  // stuck on '' (= "Auto-detect") forever. This effect promotes the stored
  // value into formData once, on first observation. We only hydrate from
  // empty → real-value (never overwrite a value the user just picked), so
  // editing mid-load is safe.
  useEffect(() => {
    if (
      profile?.u_preferred_language &&
      !formData.preferredLanguage
    ) {
      setFormData((prev) => ({ ...prev, preferredLanguage: profile.u_preferred_language }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.u_preferred_language]);

  // Optional: Refresh latest user data from backend
  const refreshProfile = async () => {
    const res = await fetch(`/api/profile/${profile.u_id}`, { credentials: 'include' });
    const updated = await res.json();
    if (updated) setProfile(updated);
  };

const [cityInput, setCityInput] = useState('');

  const handleUserInfoSave = async (e) => {
    const btn = e.currentTarget;
    const origText = btn.textContent;
    const origBg = btn.style.backgroundColor;
    btn.textContent = 'Saving...';
    btn.style.backgroundColor = '#6b7280';
    btn.disabled = true;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/profile/update-user-only`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          userId: profile.u_id,
          userData: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            jobTitle: formData.jobTitle,
            phoneNumber: formData.phoneNumber,
            profilePicture: null,
            // Empty string in the dropdown = "Auto-detect" → send null so the
            // backend clears u_preferred_language and lets auto-learning fire
            // from explicit brief.tone usage. Any other value is one of the
            // four supported codes ('en'|'es'|'ja'|'th') and persists.
            preferredLanguage: formData.preferredLanguage === '' ? null : formData.preferredLanguage,
          },
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSaveToast({ text: 'Saved successfully!', type: 'success' });
        updateProfile({
          u_first_name: formData.firstName,
          u_last_name: formData.lastName,
          u_job_title: formData.jobTitle,
          u_phone_number: formData.phoneNumber,
          u_preferred_language: formData.preferredLanguage === '' ? null : formData.preferredLanguage,
        });

        // FloatingToast auto-dismisses the success notice; we still want
        // to close the section after a beat so the user lands back on the
        // main menu.
        setTimeout(() => setActiveSection(null), 2000);

      } else {
        setSaveToast({ text: 'Something went wrong. Please try again.', type: 'error' });
      }
    } catch (error) {
      console.error('Save error:', error);
      setSaveToast({ text: 'Server error while saving.', type: 'error' });
    } finally {
      btn.textContent = origText;
      btn.style.backgroundColor = origBg;
      btn.disabled = false;
    }
  };
  const handleBusinessInfoSave = async (e) => {
    const btn = e.currentTarget;
    const origText = btn.textContent;
    const origBg = btn.style.backgroundColor;
    btn.textContent = 'Saving...';
    btn.style.backgroundColor = '#6b7280';
    btn.disabled = true;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/profile/update-business`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({
          userId: profile.u_id,
          userData: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            jobTitle: formData.jobTitle,
            phoneNumber: formData.phoneNumber,
            profilePicture: null
          },
          businessData: {
            name: formDataBusiness.companyName,
            phone: formDataBusiness.phone,
            address: formDataBusiness.address,
            city: formDataBusiness.city,
            state: formDataBusiness.state,
            subdistrict: formDataBusiness.subdistrict,
            zipCode: formDataBusiness.zip,
            country: formDataBusiness.country,
            addressComponents: formDataBusiness.addressComponents,
            businessIndustry: formDataBusiness.industry,
            industriesServed: null,
            website: (formDataBusiness.website || '').trim(),
            showAddress: formDataBusiness.showAddress // Add this line
          }
        }),
      });

      const result = await response.json();

      if (result.success) {
        setSaveToast({ text: 'Saved successfully!', type: 'success' });
        // FloatingToast auto-dismisses the success notice; still close the
        // section after a beat so the user lands back on the main menu.
        setTimeout(() => setActiveSection(null), 2000);
        
        // Mirror into Zustand (top-level + nested business)
        const trimmedWebsite = (formDataBusiness.website || '').trim();
        updateProfile({
          b_name: formDataBusiness.companyName,
          b_phone: formDataBusiness.phone,
          b_address: formDataBusiness.address,
          b_city: formDataBusiness.city,
          b_state: formDataBusiness.state,
          b_subdistrict: formDataBusiness.subdistrict,
          b_zip_code: formDataBusiness.zip,
          b_country: formDataBusiness.country,
          b_address_components: formDataBusiness.addressComponents,
          b_business_industry: formDataBusiness.industry,
          b_website: trimmedWebsite,
          b_show_address: formDataBusiness.showAddress,
          business: {
            b_name: formDataBusiness.companyName,
            b_phone: formDataBusiness.phone,
            b_address: formDataBusiness.address,
            b_city: formDataBusiness.city,
            b_state: formDataBusiness.state,
            b_subdistrict: formDataBusiness.subdistrict,
            b_zip_code: formDataBusiness.zip,
            b_country: formDataBusiness.country,
            b_address_components: formDataBusiness.addressComponents,
            b_business_industry: formDataBusiness.industry,
            b_website: trimmedWebsite,
            b_show_address: formDataBusiness.showAddress
          }
        });

        // Kick off geocoding on the server (does not block UI)
        fetch(`/api/geo/resolve-business-coords`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            userId: profile.u_id,
            address: formDataBusiness.address,
            subdistrict: formDataBusiness.subdistrict,
            city: formDataBusiness.city,
            state: formDataBusiness.state,
            zip: formDataBusiness.zip
          })
        })
          .then(r => r.json())
          .then(j => console.log('[CLIENT GEO] result:', j))
          .catch(err => console.warn('[CLIENT GEO] error:', err));
      }

    } catch (error) {
      console.error('❌ Error updating business info:', error);
      setSaveToast({ text: 'Server error while saving.', type: 'error' });
    } finally {
      btn.textContent = origText;
      btn.style.backgroundColor = origBg;
      btn.disabled = false;
    }
  };


  const welcomeMessage = `All right, ${profile?.u_first_name || ''}! What would you like to change in your profile today?`;
  if (!readyToShow) return null;
  return (
    <div
      style={{
        position: 'fixed',
        top:  0,
        left: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '300px',
        backgroundColor: 'rgba(0, 0, 0, 0)',
        zIndex: 1000,
      }}
    >
      {/* Floating save-feedback toast — fixed-position, never shifts the
          underlying UI. Replaces the old inline-banner saveMessage pattern
          that pushed each section's content downward on save. zIndex set
          ABOVE the assistant overlay (1000) so it floats over everything. */}
      <FloatingToast toast={saveToast} onDismiss={() => setSaveToast(null)} zIndex={1100} />

      {/* Enrichment-in-progress banner — auto-shows on fresh signup with
          empty b_about_us, auto-hides when the background analysis lands
          (or after 90s timeout). NOT dismissible — there's nothing the user
          can do, and letting them dismiss it would imply 'nothing's
          happening' when actually it IS happening. Lower z-index than
          save toast (1050 vs 1100) so a save toast can briefly cover it
          if both fire near-simultaneously. */}
      {enrichmentVisible && (
        <FloatingToast
          toast={{ text: enrichmentMsg, type: 'info' }}
          onDismiss={null}
          autoDismissMs={0}
          zIndex={1050}
        />
      )}

      <DraggableContainer onClose={onClose} title="Profile Assistant">
        <Content>
         
        <div style={{ textAlign: 'left', width: '100%' }}>
{!activeSection && (
  <div style={{ textAlign: 'left', width: '100%' }}>
    {showMenu ? (
      <p>{welcomeMessage}</p>
    ) : (
      <>
        <p>{welcomeMessage}</p>
        {setTimeout(() => setShowMenu(true), 50)}
      </>
    )}
  </div>
)}


        </div>


        {showMenu && !activeSection && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', width: '100%' }}>
            <button
            onClick={() => setActiveSection('user')}
            style={{
                backgroundColor: '#00aef7',
                color: 'white',
                padding: '6px 16px',
                borderRadius: '8px',
                fontWeight: '600',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.3s',
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#0098d8'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#00aef7'}
            >
            Update my user info
            </button>

            <button
              onClick={() => !isEmployee && setActiveSection('business')}
              disabled={isEmployee}
              title={tooltipMessage}
              style={{
                backgroundColor: '#00aef7',
                color: 'white',
                padding: '6px 16px',
                borderRadius: '8px',
                fontWeight: '600',
                border: 'none',
                cursor: isEmployee ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.3s',
                opacity: isEmployee ? 0.4 : 1
              }}
            >
              Update my business info
            </button>


            <button
              onClick={async () => {
                if (isEmployee) return;
                try {
                  const res = await fetch(`/api/profile/business/${profile.u_id}`, { credentials: 'include' });
                  const data = await res.json();
                  if (data?.b_about_us) {
                    setAboutUsContent(data.b_about_us);
                  } else {
                    setAboutUsContent('');
                  }
                } catch (err) {
                  console.error('❌ Failed to fetch b_about_us:', err);
                  setAboutUsContent('');
                }
                setDescriptionModalOpen(true);
              }}
              disabled={isEmployee}
              title={tooltipMessage}
              style={{
                backgroundColor: '#00aef7',
                color: 'white',
                padding: '6px 16px',
                borderRadius: '8px',
                fontWeight: '600',
                border: 'none',
                cursor: isEmployee ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.3s',
                opacity: isEmployee ? 0.4 : 1
              }}
            >
              Update my business description
            </button>


            <button
              onClick={() => setActiveSection('uploadUserPhoto')}
            style={{
                backgroundColor: '#00aef7',
                color: 'white',
                padding: '6px 16px',
                borderRadius: '8px',
                fontWeight: '600',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.3s',
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#0098d8'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#00aef7'}
            >
            Upload new business class photo
            </button>
            <button
              onClick={() => !isEmployee && setActiveSection('uploadBusinessLogo')}
              disabled={isEmployee}
              title={tooltipMessage}
              style={{
                backgroundColor: '#00aef7',
                color: 'white',
                padding: '6px 16px',
                borderRadius: '8px',
                fontWeight: '600',
                border: 'none',
                cursor: isEmployee ? 'not-allowed' : 'pointer',
                transition: 'background-color 0.3s',
                opacity: isEmployee ? 0.4 : 1
              }}
            >
              Upload new business logo
            </button>

        </div>
        )}
{activeSection === 'user' && (
    <div style={{ width: '100%', marginTop: '0px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <h2 style={{
        fontSize: '1.1rem',
        fontWeight: '600',
        marginTop: '6px',
        marginBottom: '6px',
        textAlign: 'center'
        }}>
        User Profile Information
        </h2>

    {/* Save feedback rendered as a fixed-position FloatingToast at the top
        of the component (mounted in the main return). Removed the old
        inline banner that pushed the section's content downward on save. */}

    <input
    type="text"
    placeholder="First Name"
    value={formData.firstName}
    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
    style={inputStyle}
    />


      <input
        type="text"
        placeholder="Last Name"
        value={formData.lastName}
        onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
        style={inputStyle}
      />

      <input
        type="text"
        placeholder="Job Title"
        value={formData.jobTitle}
        onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
        style={inputStyle}
      />

      <input
        type="text"
        placeholder="Phone Number"
        value={formData.phoneNumber}
        onChange={(e) => {
          const formatted = formatPhoneNumber(e.target.value);
          setFormData({ ...formData, phoneNumber: formatted });
        }}
        style={inputStyle}
      />

      {/* Preferred language — drives the default language for AI-generated
          captions, landing pages, and the Design Center assistant chat.
          'Auto-detect' (empty string here, NULL on the backend) lets the
          system pick from business context (Thai biz name -> Thai) and
          auto-learn from explicit brief.tone usage. Per-post override is
          still always available via brief.tone (e.g., a Thai default user
          can type 'in Japanese please' for a single post without changing
          this default). */}
      <label style={{ fontSize: '0.85rem', color: '#555', marginTop: '4px', marginBottom: '-2px' }}>
        Preferred language
      </label>
      <select
        value={formData.preferredLanguage}
        onChange={(e) => setFormData({ ...formData, preferredLanguage: e.target.value })}
        style={{
          ...inputStyle,
          appearance: 'auto',
          cursor: 'pointer',
        }}
      >
        <option value="">Auto-detect</option>
        <option value="en">English</option>
        <option value="es">Español (Spanish)</option>
        <option value="ja">日本語 (Japanese)</option>
        <option value="th">ไทย (Thai)</option>
      </select>

      <button onClick={handleUserInfoSave} style={saveButtonStyle}>
        Save Changes
      </button>

      <button onClick={() => setActiveSection(null)} style={backButtonStyle}>
        ← Back to main menu
      </button>
    </div>
)}

{activeSection === 'business' && (
    <div style={{ width: '100%', marginTop: '0px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
    <h2 style={{
        fontSize: '1.1rem',
        fontWeight: '600',
        marginTop: '0px',
        marginBottom: '6px',
        textAlign: 'center'
    }}>
        Business Profile Information
    </h2>

    {/* Save feedback rendered as a fixed-position FloatingToast at the top
        of the component (mounted in the main return). No inline banner. */}

    <input
    type="text"
    placeholder="Company Name"
    value={formDataBusiness.companyName}
    onChange={(e) => setFormDataBusiness({ ...formDataBusiness, companyName: e.target.value })}
    style={inputStyle}
    />
    <label style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: '2px' }}>
      

    </label>

{/* Country-driven address form. Country sits at the top; choosing it swaps the
    field set to that country's postal format (US ZIP / Thailand province→
    district→sub-district cascade with auto-postal / generic international).
    See BusinessAddressFields.jsx. */}
<BusinessAddressFields
  value={formDataBusiness}
  onChange={(patch) => setFormDataBusiness((prev) => ({ ...prev, ...patch }))}
  inputStyle={inputStyle}
/>

        <input
        type="text"
        placeholder="Business Phone"
        value={formDataBusiness.phone}
        onChange={(e) => {
          const formatted = formatPhoneNumber(e.target.value);
          setFormDataBusiness({ ...formDataBusiness, phone: formatted });
        }}
        style={inputStyle}
        />

<input
  type="text"
  placeholder="Industry"
  value={formDataBusiness.industry}
  onChange={(e) =>
    setFormDataBusiness({ ...formDataBusiness, industry: e.target.value })
  }
  style={{
    ...inputStyle,
    opacity: 1  // <-- Always fully visible
  }}
/>

{/* ── Website field (Option C — post-signup b_website edit) ──
    Brett's "no re-validation" policy applies here: signup-time gates
    (competitor blocklist, big-company blocklist, public-email match,
    shared-employee blocklist) are NOT re-run when this field is changed.
    They were skipped at signup for platform-hosted flows anyway and
    aren't checked on any read path post-signup, so editing here is safe.

    Hint banner: when current b_website looks like a platform-hosted URL
    (Google Maps / FB / IG / TikTok / LinkedIn), surface a nudge encouraging
    the user to enter their real domain. Especially valuable for GBP signups
    because the Review Importer uses website-domain-match as its ownership
    proof — if b_website is the GBP URL itself, the match always fails. */}
{(() => {
  const platformLabel = detectPlatformHostedWebsite(formDataBusiness.website);
  if (!platformLabel) return null;
  return (
    <div style={{
      backgroundColor: '#FEF3C7',
      border: '1px solid #FCD34D',
      borderRadius: '6px',
      padding: '8px 10px',
      fontSize: '0.85rem',
      color: '#78350F',
      lineHeight: 1.4,
    }}>
      Your current website is your <strong>{platformLabel}</strong> link. If you have a separate website (like <em>yourbusiness.com</em>), enter it below — it makes your profile look cleaner and unlocks features like Google review imports.
    </div>
  );
})()}

<input
  type="text"
  placeholder="Website (e.g., yourbusiness.com)"
  value={formDataBusiness.website}
  onChange={(e) =>
    setFormDataBusiness({ ...formDataBusiness, website: e.target.value })
  }
  style={inputStyle}
/>

    <button
    onClick={handleBusinessInfoSave}
    style={saveButtonStyle}
    >
    Save Changes
    </button>


    <button
    onClick={() => setActiveSection(null)}
    style={backButtonStyle}
    >
    ← Back to main menu
    </button>

  </div>
)}
{activeSection === 'uploadUserPhoto' && (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      width: '100%',
      marginTop: '4px',
      gap: '10px'
    }}
  >
    <h2 style={{ fontSize: '1.05rem', fontWeight: '600', margin: 0, padding: 0, textAlign: 'center' }}>
      Upload your profile photo
    </h2>

{profile?.u_profile_picture ? (
  <img
    src={`${window.location.origin}${profile.u_profile_picture}`}
    alt="Profile"
    className="rounded-full border-2 border-[#00aef7]/30 object-cover"
    style={{
      width: '175px',
      height: '175px',
      objectFit: 'cover',
      display: 'block',
      margin: '0 auto',
      borderRadius: '50%'
    }}
    onError={(e) => {
      console.error('IMAGE LOAD FAILED:', e.target.src);
      e.target.src = '/images/default-avatar.png';
    }}
  />
) : (
  <img
    src="/images/default-avatar.png"
    alt="Default Avatar"
    style={{
      width: '175px',
      height: '175px',
      objectFit: 'cover',
      display: 'block',
      margin: '0 auto',
      borderRadius: '50%'
    }}
  />
)}

    <div style={{ marginTop: '4px', width: '100%' }}>
      <input
        type="file"
        accept=".jpg,.jpeg,.png,.webp,.heic,.heif"
        style={{ display: 'none' }}
        id="upload-profile-input"
        onChange={async (e) => {
          const file = e.target.files[0];
          if (!file) return;

          // ---- Client-side guardrails ----
          const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
          const ALLOWED_EXT = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'];
          const MAX_BYTES = 5 * 1024 * 1024; // 5MB

          const nameLower = file.name.toLowerCase();
          const hasAllowedExt = ALLOWED_EXT.some(ext => nameLower.endsWith(ext));
          // Accept if MIME matches OR extension matches (Windows doesn't report HEIC MIME)
          if (!ALLOWED_MIME.includes(file.type) && !hasAllowedExt) {
            alert('Only JPG, PNG, WebP, and HEIC files are allowed.');
            e.target.value = ''; // reset
            return;
          }
          if (file.size > MAX_BYTES) {
            alert('File is too large (max 5MB).');
            e.target.value = '';
            return;
          }

          // (Optional) pixel guard: avoid decompression-bomb sized images
          // Skip for HEIC/HEIF — browsers can't decode them; server Sharp handles the check
          const canDecode = !nameLower.endsWith('.heic') && !nameLower.endsWith('.heif');
          if (canDecode) {
            const blobUrl = URL.createObjectURL(file);
            const img = new Image();
            img.src = blobUrl;
            await new Promise((res, rej) => {
              img.onload = res;
              img.onerror = () => rej(new Error('Image load failed'));
            });
            URL.revokeObjectURL(blobUrl);
            if (img.width * img.height > 40_000_000) { // >40MP
              alert('Image dimensions are too large.');
              e.target.value = '';
              return;
            }
          }
          // ---- End guardrails ----

          const formData = new FormData();
          formData.append('profilePicture', file);

          try {
            const response = await fetch(`/api/userupload/user-profile?userId=${profile.u_id}`, {
              method: 'POST',
              credentials: 'include',
              body: formData
            });

            const result = await response.json();
            if (result?.url) {
            // Mirror into Zustand (preserve existing business fields)
            updateProfile({ u_profile_picture: result.url });
            setSaveToast({ text: 'Saved successfully!', type: 'success' });
            } else {
              setSaveToast({ text: result?.error || 'Upload failed. Please try again.', type: 'error' });
            }

          } catch (err) {
            console.error('❌ Upload failed:', err);
            setSaveToast({ text: 'Upload failed. Please try again.', type: 'error' });
          } finally {
            e.target.value = ''; // allow re-selecting same file
          }
        }}
      />


      <button
        onClick={() => document.getElementById('upload-profile-input').click()}
        style={{ ...saveButtonStyle, marginTop: '0px', width: '100%' }}
      >
        Upload New Photo
      </button>
    </div>

    <button
      onClick={() => setActiveSection(null)}
      style={{ ...backButtonStyle, marginTop: '0px', width: '100%' }}
    >
      ← Back to main menu
    </button>
  </div>
)}
{activeSection === 'uploadBusinessLogo' && (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      width: '100%',
      paddingTop: '25px',
      gap: '10px'
    }}
  >
    {/* Save feedback rendered as a fixed-position FloatingToast at the top
        of the component (mounted in the main return). The old inline
        banner pushed the entire upload section downward whenever the toast
        appeared — exactly what the new pattern is designed to avoid. */}

    {profile?.b_logo ? (
      <img
        src={`${process.env.REACT_APP_API_URL}${profile.b_logo}`}
        alt="Business Logo"
        style={{
          width: '80%',
          height: 'auto',
          objectFit: 'contain',
          backgroundColor: '#fff',
          display: 'block',
          margin: '0 auto',
          borderRadius: '8px'
        }}
        onError={(e) => {
          console.warn('❌ Logo failed to load, hiding preview.');
          e.target.style.display = 'none';
        }}
      />
    ) : (
      <img
        src="/images/Logo_Placeholder.jpg"
        alt="Placeholder"
        style={{
          width: '80%',
          height: 'auto',
          objectFit: 'contain',
          borderRadius: '8px'
        }}
      />
    )}

    <div style={{ marginTop: '4px', width: '100%' }}>
      <input
        type="file"
        accept=".jpg,.jpeg,.png,.webp,.heic,.heif"
        style={{ display: 'none' }}
        id="upload-business-logo-input"
        onChange={async (e) => {
          const file = e.target.files[0];
          if (!file) return;

          // ---- Client-side guardrails ----
          const ALLOWED_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
          const ALLOWED_EXT = ['.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif'];
          const MAX_BYTES = 5 * 1024 * 1024; // 5MB

          const nameLower = file.name.toLowerCase();
          const hasAllowedExt = ALLOWED_EXT.some(ext => nameLower.endsWith(ext));
          // Accept if MIME matches OR extension matches (Windows doesn't report HEIC MIME)
          if (!ALLOWED_MIME.includes(file.type) && !hasAllowedExt) {
            alert('Only JPG, PNG, WebP, and HEIC files are allowed.');
            e.target.value = '';
            return;
          }
          if (file.size > MAX_BYTES) {
            alert('File is too large (max 5MB).');
            e.target.value = '';
            return;
          }

          // Skip pixel guard for HEIC/HEIF — browsers can't decode them; server Sharp handles the check
          const canDecodeLogo = !nameLower.endsWith('.heic') && !nameLower.endsWith('.heif');
          if (canDecodeLogo) {
            const blobUrl = URL.createObjectURL(file);
            const img = new Image();
            img.src = blobUrl;
            await new Promise((res, rej) => {
              img.onload = res;
              img.onerror = () => rej(new Error('Image load failed'));
            });
            URL.revokeObjectURL(blobUrl);
            if (img.width * img.height > 40_000_000) {
              alert('Image dimensions are too large.');
              e.target.value = '';
              return;
            }
          }
          // ---- End guardrails ----

          try {
            setSaveToast({ text: 'Uploading...', type: 'info' });
            const formData = new FormData();
            formData.append('businessLogo', file);
            formData.append('businessId', profile.b_id);

            const response = await fetch(
              `/api/businessupload/business-logo?businessId=${profile.b_id}`,
              { method: 'POST', credentials: 'include', body: formData }
            );

            const result = await response.json();

            if (result?.url) {
              updateProfile({
                b_logo: result.url,
                business: { b_logo: result.url }
              });
              // FloatingToast auto-dismisses; no setTimeout needed.
            } else {
              setSaveToast({ text: result?.error || 'Upload failed', type: 'error' });
            }



          } catch (err) {
            setSaveToast({ text: 'Upload failed', type: 'error' });
            console.error('❌ Upload failed:', err);
          } finally {
            e.target.value = '';
          }
        }}
      />


      <button
        onClick={() => document.getElementById('upload-business-logo-input').click()}
        style={{ ...saveButtonStyle, marginTop: '0px', width: '100%' }}
      >
        Upload New Logo
      </button>
    </div>

    <button
      onClick={() => setActiveSection(null)}
      style={{ ...backButtonStyle, marginTop: '4px', width: '100%' }}
    >
      ← Back to main menu
    </button>
  </div>
)}

          {descriptionModalOpen && (
            <BusinessDescriptionModal
              opened={true}
              onClose={() => setDescriptionModalOpen(false)}
              initialContent={aboutUsContent}
              onSave={async (updatedContent) => {
                try {
                  const response = await fetch(`/api/profile/update-business-description`, {
                    method: 'PUT',
                    headers: {
                      'Content-Type': 'application/json'
                    },
                    credentials: 'include',
                    body: JSON.stringify({
                      businessId: profile?.b_id,
                      aboutUs: updatedContent
                    })
                  });

                  const result = await response.json();
                  if (response.ok) {
                    setSaveToast({ text: 'Saved successfully!', type: 'success' });

                    // Manually inject new business description into context
                  updateProfile({
                    b_about_us: updatedContent,
                    business: {
                      b_about_us: updatedContent
                    }
                  });

                    // FloatingToast auto-dismisses; no setTimeout needed.
                  }
                    else {
                    console.error('❌ Save failed:', result);
                    setSaveToast({ text: 'Save failed. Please try again.', type: 'error' });
                  }
                } catch (error) {
                  console.error('❌ Error saving business description:', error);
                  setSaveToast({ text: 'Server error while saving.', type: 'error' });
                } finally {
                  setDescriptionModalOpen(false);
                }
              }}

            />
          )}

        </Content>
      </DraggableContainer>
    </div>
  );
};
const inputStyle = {

  padding: '0px 10px',       // reduced vertical padding
  borderRadius: '6px',
  border: '1px solid #ccc',
  fontSize: '1rem',
  height: '30px',            // explicitly sets shorter height
  boxSizing: 'border-box'
};


const saveButtonStyle = {
  backgroundColor: '#00aef7',
  color: 'white',
  padding: '6px',
  fontWeight: '600',
  borderRadius: '6px',
  border: 'none',
  cursor: 'pointer',
  fontSize: '0.95rem'  // slightly smaller text
};

const backButtonStyle = {
  backgroundColor: '#ccc',
  color: '#333',
  padding: '5px',
  fontWeight: '600',
  borderRadius: '6px',
  border: 'none',
  cursor: 'pointer',
  fontSize: '0.95rem'
};

export default OngoingProfileAssistant;


